import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/DriveFolderUploadOutlined";
import { useCallback, useEffect, useRef, useState } from "react";
import { PageContainer, PageHeader } from "../components/PageHeader";
import EditorCard from "../components/template/EditorCard";
import {
  foyerApi,
  type BatchItemView,
  type BatchView,
  type IngestReadiness,
} from "../http/foyer";
import { packageFindingText } from "../components/distribution/findings";
import { formatBatchEta, type BatchStatus } from "@shared/bulk-ingest";
import { uploadInBlocks } from "../lib/blockUpload";
import { tokens } from "../theme";

/**
 * Bulk ingest — many titles through the lifecycle that works for one
 * (`BE-31`).
 *
 * ## The screen has one job the server cannot do for it: be honest about time
 *
 * Encore is serial and a transcode is roughly a quarter of an hour, so a
 * 300-title package is **days**, before Foyer yields a single slot back to the
 * portal. That number is on the first screen, next to the button, because the
 * alternative is somebody discovering it on day two — and because it is the
 * reason pause and resume exist at all rather than only cancel.
 *
 * ## Findings come before anything is dispatched
 *
 * Scan is read-only and repeatable: it pairs what is actually in storage,
 * validates it, and creates nothing. An operator fixes a sidecar, re-uploads
 * it, rescans, and watches the finding go — with no catalogue rows minted along
 * the way. Only Start creates anything, and by then every refusal has already
 * been shown with the file it concerns.
 */

const STATUS_COPY: Record<BatchStatus, { label: string; tone: string }> = {
  empty: { label: "No files", tone: tokens.inkMuted },
  draft: { label: "Draft", tone: tokens.inkMuted },
  all_blocked: { label: "Nothing startable", tone: tokens.error },
  running: { label: "Running", tone: tokens.brand },
  paused: { label: "Paused", tone: tokens.warning },
  cancelled: { label: "Cancelled", tone: tokens.inkMuted },
  completed: { label: "Completed", tone: tokens.success },
  partly_failed: { label: "Finished with failures", tone: tokens.warning },
};

function StatusChip({ status }: { status: BatchStatus }) {
  const copy = STATUS_COPY[status];
  return (
    <Chip
      size="small"
      variant="outlined"
      label={copy.label}
      sx={{ borderColor: copy.tone, color: copy.tone, fontWeight: 700 }}
      data-testid={`batch-status-${status}`}
    />
  );
}

function FindingList({ findings }: { findings: BatchItemView["findings"] }) {
  if (findings.length === 0) return null;
  return (
    <Stack spacing={0.5} sx={{ mt: 0.75 }}>
      {findings.map((f, i) => (
        <Typography
          key={`${f.code}-${i}`}
          sx={{
            fontSize: 11,
            color: f.severity === "blocking" ? tokens.error : tokens.inkMuted,
          }}
        >
          {f.severity === "blocking" ? "✕ " : "• "}
          {packageFindingText(f)}
        </Typography>
      ))}
    </Stack>
  );
}

function ItemRow({ item }: { item: BatchItemView }) {
  return (
    <Box
      sx={{
        py: 1.25,
        borderBottom: `1px solid ${tokens.divider}`,
      }}
      data-testid={`batch-item-${item.key}`}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: tokens.ink }}>
            {item.title}
          </Typography>
          <Typography
            sx={{
              fontSize: 11,
              color: tokens.inkMuted,
              fontFamily: "monospace",
            }}
          >
            {item.videoPath}
            {item.assetId ? ` → ${item.assetId}.mp4` : ""}
          </Typography>
        </Box>
        <Chip
          size="small"
          variant="outlined"
          label={
            item.blocked ? "refused" : item.phase ? item.phase : "not started"
          }
          color={
            item.blocked
              ? "error"
              : item.phase === "completed"
                ? "success"
                : item.phase === "failed"
                  ? "error"
                  : "default"
          }
        />
      </Stack>

      {item.phase === "transcoding" && item.progress !== null && (
        <LinearProgress
          variant="determinate"
          value={Math.min(100, Math.max(0, item.progress))}
          sx={{ mt: 1, borderRadius: 1 }}
        />
      )}

      <FindingList findings={item.findings} />

      {/* Two different failures, kept apart. `error` is Foyer refusing to
          create the row or move the file; `jobError` is the pipeline. Folding
          them together loses which half to go and look at. */}
      {item.error && (
        <Typography sx={{ fontSize: 11, color: tokens.error, mt: 0.5 }}>
          ✕ {item.error}
        </Typography>
      )}
      {item.jobError && (
        <Typography sx={{ fontSize: 11, color: tokens.error, mt: 0.5 }}>
          ✕ {item.jobError}
        </Typography>
      )}
    </Box>
  );
}

/** Relative path inside the drop, so a folder upload keeps its layout. */
function relativePathOf(file: File): string {
  const withPath = file as File & { webkitRelativePath?: string };
  const raw = withPath.webkitRelativePath || file.name;
  // A directory picker prefixes the chosen folder's own name. Dropping it is
  // what makes `The Film/movie.mp4` pair with `The Film/metadata.json` rather
  // than everything living one level too deep.
  const slash = raw.indexOf("/");
  return slash > 0 ? raw.slice(slash + 1) : raw;
}

function BatchDetail({
  batch,
  readiness,
  onChanged,
}: {
  batch: BatchView;
  readiness: IngestReadiness | null;
  onChanged: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [packages, setPackages] = useState<Array<{ id: string; name: string }>>(
    []
  );
  const folderInput = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    foyerApi
      .listIngestEntitlements()
      .then((a) => setPackages(a.collections))
      .catch(() => setPackages([]));
  }, []);

  const draft = batch.intent === "draft";
  const { rollup } = batch;

  async function run(label: string, work: () => Promise<unknown>) {
    setError(null);
    setBusy(label);
    try {
      await work();
      await onChanged();
    } catch (e) {
      const err = e as { code?: string; message: string; unknown?: string[] };
      setError(
        err.code === "unknown_entitlements"
          ? `No package resolves for ${(err.unknown ?? []).join(", ")}. Nothing was started: every title would have been invisible.`
          : err.code === "storage_not_configured"
            ? "Storage is not configured for ingest yet, so there is nowhere to put the files."
            : err.code === "nothing_to_start"
              ? "Every item was refused by validation, so there is nothing to start."
              : err.message
      );
    } finally {
      setBusy(null);
    }
  }

  async function uploadFolder(files: FileList) {
    setError(null);
    const list = Array.from({ length: files.length }, (_, i) => files[i]);
    for (const [index, file] of list.entries()) {
      setBusy(`Uploading ${index + 1} of ${list.length}: ${file.name}`);
      const path = relativePathOf(file);
      // Sequential on purpose. The portal's own bulk client uploads with
      // concurrency 1, and matching it keeps a package that took an hour to
      // upload from saturating a customer's line.
      const { uploadUrl } = await foyerApi.batchUploadUrl(batch.id, path);
      await uploadInBlocks(uploadUrl, file, {
        contentType: file.type || "application/octet-stream",
      });
    }
    setBusy("Scanning the package…");
    await foyerApi.scanBatch(batch.id);
    await onChanged();
    setBusy(null);
  }

  return (
    <EditorCard
      eyebrow={batch.name}
      hint={`Started by ${batch.createdBy} · ${new Date(batch.createdAt).toISOString().slice(0, 10)}`}
      action={<StatusChip status={rollup.status} />}
      data-testid={`batch-detail-${batch.id}`}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, fontSize: 12 }}>
          {error}
        </Alert>
      )}

      {readiness && !readiness.ready && (
        <Alert severity="warning" sx={{ mb: 2, fontSize: 12 }}>
          {readiness.explanation}
        </Alert>
      )}

      {/* The honest first screen. */}
      <Stack
        direction="row"
        spacing={3}
        sx={{ mb: 2 }}
        flexWrap="wrap"
        useFlexGap
      >
        <Counter label="Titles" value={rollup.total} />
        <Counter label="Refused" value={rollup.blocked} tone={tokens.error} />
        <Counter label="Waiting" value={rollup.waiting} />
        <Counter label="Running" value={rollup.running} tone={tokens.brand} />
        <Counter
          label="Published"
          value={rollup.completed}
          tone={tokens.success}
        />
        <Counter label="Failed" value={rollup.failed} tone={tokens.error} />
      </Stack>

      {rollup.remaining > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2, fontSize: 12 }}
          data-testid="batch-eta"
        >
          Roughly <b>{formatBatchEta(rollup.etaMinutes)}</b> of transcoding left
          — the farm runs one job at a time, and Foyer waits whenever the old
          portal is using it, so this is a floor rather than an estimate.
        </Alert>
      )}

      {batch.packageFindings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2, fontSize: 12 }}>
          <FindingList findings={batch.packageFindings} />
        </Alert>
      )}

      {draft && (
        <Box
          sx={{
            border: `1px dashed ${tokens.divider}`,
            borderRadius: 2,
            p: 3,
            textAlign: "center",
            mb: 2,
            bgcolor: tokens.brandTintSoft,
          }}
        >
          <UploadIcon sx={{ fontSize: 28, color: tokens.inkMuted }} />
          <Typography sx={{ fontSize: 13, fontWeight: 700, mt: 0.5 }}>
            The package folder
          </Typography>
          <Typography sx={{ fontSize: 11, color: tokens.inkMuted, mt: 0.5 }}>
            One folder per title, or MP4s side by side with matching sidecars.
            Files go straight to storage and keep their own names while the
            package is being assembled — Foyer renames each master to{" "}
            <Box component="span" sx={{ fontFamily: "monospace" }}>
              {"{assetId}.mp4"}
            </Box>{" "}
            when the batch starts, which is the name delivery keys on.
          </Typography>

          <Box sx={{ mt: 1.5, textAlign: "left" }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, mb: 0.5 }}>
              Packages every title in this batch joins
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {packages.map((p) => (
                <Chip
                  key={p.id}
                  size="small"
                  label={p.name}
                  data-testid={`batch-package-${p.id}`}
                  color={
                    batch.entitlements.includes(p.id) ? "primary" : "default"
                  }
                  variant={
                    batch.entitlements.includes(p.id) ? "filled" : "outlined"
                  }
                  onClick={() =>
                    void run("packages", () =>
                      foyerApi.setBatchPackages(
                        batch.id,
                        batch.entitlements.includes(p.id)
                          ? batch.entitlements.filter((x) => x !== p.id)
                          : [...batch.entitlements, p.id]
                      )
                    )
                  }
                />
              ))}
            </Stack>
            {batch.entitlements.length === 0 && (
              <Typography
                sx={{ fontSize: 11, color: tokens.warning, mt: 0.5 }}
                data-testid="batch-no-package-warning"
              >
                {packages.length === 0
                  ? "There are no packages in Foyer yet. Everything in this batch will be transcoded and delivered, and no guest will be able to see any of it."
                  : "No package selected — every title will be transcoded and delivered, and no guest will be able to see it."}
              </Typography>
            )}
          </Box>

          <input
            ref={folderInput}
            type="file"
            hidden
            multiple
            data-testid="batch-folder"
            // Non-standard and deliberately used: a package IS a folder, and
            // asking somebody to select 900 files by hand is not a workflow.
            {...({
              webkitdirectory: "",
              directory: "",
            } as Record<string, string>)}
            onChange={(e) => {
              const files = e.target.files;
              e.target.value = "";
              if (files && files.length > 0) {
                void run("upload", () => uploadFolder(files));
              }
            }}
          />

          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            sx={{ mt: 1.5 }}
          >
            <Button
              variant="contained"
              size="small"
              disabled={Boolean(busy)}
              onClick={() => folderInput.current?.click()}
              data-testid="batch-choose-folder"
            >
              Choose package folder
            </Button>
            <Button
              size="small"
              disabled={Boolean(busy)}
              onClick={() =>
                void run("scan", () => foyerApi.scanBatch(batch.id))
              }
              data-testid="batch-rescan"
            >
              Rescan
            </Button>
          </Stack>
        </Box>
      )}

      {busy && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress sx={{ borderRadius: 1 }} />
          <Typography
            sx={{ fontSize: 11, color: tokens.inkMuted, mt: 0.5 }}
            data-testid="batch-busy"
          >
            {busy}
          </Typography>
        </Box>
      )}

      <Divider sx={{ mb: 1.5 }} />

      <Stack
        direction="row"
        spacing={1}
        sx={{ mb: 2 }}
        flexWrap="wrap"
        useFlexGap
      >
        {draft && (
          <Button
            variant="contained"
            size="small"
            disabled={Boolean(busy) || rollup.total === 0}
            onClick={() =>
              void run("start", () => foyerApi.startBatch(batch.id))
            }
            data-testid="batch-start"
          >
            Start {rollup.total - rollup.blocked} title
            {rollup.total - rollup.blocked === 1 ? "" : "s"}
          </Button>
        )}
        {batch.intent === "started" && (
          <Button
            size="small"
            disabled={Boolean(busy)}
            onClick={() =>
              void run("pause", () =>
                foyerApi.setBatchIntent(batch.id, "pause")
              )
            }
            data-testid="batch-pause"
          >
            Pause
          </Button>
        )}
        {batch.intent === "paused" && (
          <Button
            size="small"
            variant="contained"
            disabled={Boolean(busy)}
            onClick={() =>
              void run("resume", () =>
                foyerApi.setBatchIntent(batch.id, "resume")
              )
            }
            data-testid="batch-resume"
          >
            Resume
          </Button>
        )}
        {batch.intent !== "cancelled" && batch.intent !== "draft" && (
          <Button
            size="small"
            color="error"
            disabled={Boolean(busy)}
            onClick={() =>
              void run("cancel", () =>
                foyerApi.setBatchIntent(batch.id, "cancel")
              )
            }
            data-testid="batch-cancel"
          >
            Cancel
          </Button>
        )}
      </Stack>

      {(batch.intent === "paused" || batch.intent === "cancelled") && (
        <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
          Titles already on the transcoder finish — Foyer cannot recall a job
          from a farm it shares. What stops is anything that had not started.
        </Alert>
      )}

      {batch.items.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
          Nothing paired yet. Upload a package folder and it will be scanned.
        </Typography>
      ) : (
        batch.items.map((item) => <ItemRow key={item.key} item={item} />)
      )}
    </EditorCard>
  );
}

function Counter({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: string;
}) {
  return (
    <Box>
      <Typography
        sx={{ fontSize: 20, fontWeight: 700, color: tone ?? tokens.ink }}
      >
        {value}
      </Typography>
      <Typography sx={{ fontSize: 11, color: tokens.inkMuted }}>
        {label}
      </Typography>
    </Box>
  );
}

export default function BulkIngestPage() {
  const [batches, setBatches] = useState<BatchView[] | null>(null);
  const [readiness, setReadiness] = useState<IngestReadiness | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");

  const load = useCallback(async () => {
    try {
      const answer = await foyerApi.listBatches();
      setBatches(answer.batches);
      setReadiness(answer.readiness);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
    // A batch is a multi-day object, so this polls slowly: the interesting
    // change is a phase moving, and that happens on a 30 s server tick.
    const timer = setInterval(() => void load(), 20_000);
    return () => clearInterval(timer);
  }, [load]);

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Distribution"
        title="Bulk ingest"
        subtitle="Submit a package of titles, see what it will and will not accept, then watch it land."
        actions={
          <Button
            variant="contained"
            size="small"
            onClick={() => setCreating(true)}
            data-testid="batch-new"
          >
            New package
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2, fontSize: 12 }}>
          {error}
        </Alert>
      )}

      {batches === null && (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={24} />
        </Stack>
      )}

      {batches?.length === 0 && (
        <EditorCard eyebrow="No packages yet">
          <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
            A package is a folder of masters with their metadata. Uploading one
            creates a catalogue title per item, transcodes each in turn and
            publishes it — one job at a time, because the transcoder is shared
            with the old portal.
          </Typography>
        </EditorCard>
      )}

      <Stack spacing={2}>
        {batches?.map((batch) =>
          batch.id === openId ? (
            <Box key={batch.id}>
              <BatchDetail
                batch={batch}
                readiness={readiness}
                onChanged={load}
              />
              <Button
                size="small"
                onClick={() => setOpenId(null)}
                sx={{ mt: 1 }}
              >
                Close
              </Button>
            </Box>
          ) : (
            <EditorCard
              key={batch.id}
              eyebrow={batch.name}
              hint={`${batch.rollup.total} titles · ${batch.rollup.completed} published${
                batch.rollup.remaining > 0
                  ? ` · ~${formatBatchEta(batch.rollup.etaMinutes)} left`
                  : ""
              }`}
              action={<StatusChip status={batch.rollup.status} />}
            >
              <Button
                size="small"
                onClick={() => setOpenId(batch.id)}
                data-testid={`batch-open-${batch.id}`}
              >
                Open
              </Button>
            </EditorCard>
          )
        )}
      </Stack>

      <Dialog
        open={creating}
        onClose={() => setCreating(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>New package</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText="A package can take days. Name it something you will recognise on Thursday."
            inputProps={{ "data-testid": "batch-name" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreating(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={name.trim() === ""}
            data-testid="batch-create"
            onClick={() => {
              void (async () => {
                try {
                  const { batch } = await foyerApi.createBatch({
                    name: name.trim(),
                  });
                  setName("");
                  setCreating(false);
                  await load();
                  setOpenId(batch.id);
                } catch (e) {
                  setError((e as Error).message);
                }
              })();
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}
